import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { CheckboxInput } from '@calendly/ui/components/checkbox-input';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { ArrowChevronDownMiniIcon, ArrowChevronUpMiniIcon } from '@calendly/ui/components/icons';
import { Item } from '@calendly/ui/components/menu';
import { BodyText } from '@calendly/ui/components/typography';
import { primaryColorL1, errorColor } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { ViewportDropdownMenu } from 'components/core/viewport_dropdown_menu';
import { EventTypeSelect } from 'features/meeting_intelligence/components/event_type_select';
import { useNotetakerSettingsContext } from 'features/meeting_intelligence/settings/notetaker_settings_provider';
import { useMeetingIntelligenceContext } from 'features/meeting_intelligence/settings/provider';
import { CheckboxWrapper, CheckboxCopyWrapper, FormStyledCheckboxLabel, DropdownWrapper, DropdownErrorWrapper } from './styles';
const DROPDOWN_OPTIONS = [{
  value: 'ALL',
  labelKey: 'publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.dropdown_options.all'
}, {
  value: 'MANUAL',
  labelKey: 'publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.dropdown_options.manual'
}, {
  value: 'OUTSIDE_COMPANY',
  labelKey: 'publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.dropdown_options.outside_company'
}, {
  value: 'HOW_SCHEDULED',
  labelKey: 'publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.dropdown_options.how_scheduled'
}];
const inlineDropdownTextCss = "igc4xvu";
const InlineDropdownText = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(inlineDropdownTextCss, props.className)} />;
});
const dropdownContainerCss = "doy6yto";
const DropdownContainer = forwardRef((props, ref) => <span ref={ref} {...props} className={classNames(dropdownContainerCss, props.className)} />);
const textContainerCss = "tkvpidb";
const TextContainer = forwardRef((props, ref) => <div ref={ref} {...props} className={classNames(textContainerCss, props.className)} />);
const bareButtonCss = "b1hivdip";
const StyledBareButton = forwardRef((props, ref) => <BareButton ref={ref} {...props} className={classNames(bareButtonCss, props.className)} />);
const iconCss = "iwfqo4t";
const eventTypeDropdownSpacingCss = "e1exdwzm";
const StyledIcon = forwardRef((props, ref) => <span ref={ref} {...props} className={classNames(iconCss, props.className)} />);
export const RecordingDefaultsBody = () => {
  const {
    recordingDefaultOption,
    setRecordingDefaultOption,
    scheduledFromCalendlyCheckbox,
    setScheduledFromCalendlyCheckbox,
    scheduledFromNonCalendlyCheckbox,
    setScheduledFromNonCalendlyCheckbox,
    eventTypes,
    hasEventError,
    actualSelectedCount,
    handleEventTypeSelect,
    handleSelectAll,
    handleClearAll,
    handleFetchMoreEvents
  } = useNotetakerSettingsContext();
  const {
    email,
    isValidCalendar
  } = useMeetingIntelligenceContext() || {};
  const [toggleDropdown, Dropdown, isExpanded] = useDropdown();
  const selectedOption = DROPDOWN_OPTIONS.find(opt => opt.value === recordingDefaultOption);
  const dropdownOptionLabel = selectedOption ? I18n.t(selectedOption.labelKey) : '';
  const handleOptionClick = value => {
    setRecordingDefaultOption(value);
    toggleDropdown();
  };
  return <div>
      <BodyText size="medium" weight="bold">
        {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.title')}
      </BodyText>
      <BodyText size="medium" color="muted">
        {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.description')}
      </BodyText>

      <TextContainer>
        <BodyText size="medium" as="span">
          {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.notetaker_should_join')}
          {' '}
        </BodyText>
        <DropdownContainer>
          <StyledBareButton onClick={toggleDropdown}>
            <InlineDropdownText>
              {dropdownOptionLabel}
            </InlineDropdownText>
            <StyledIcon>
              {isExpanded ? <ArrowChevronUpMiniIcon color={primaryColorL1} /> : <ArrowChevronDownMiniIcon color={primaryColorL1} />}
            </StyledIcon>
          </StyledBareButton>
          <Dropdown>
            <ViewportDropdownMenu pullRight>
              {DROPDOWN_OPTIONS.map(option => <Item key={option.value} onClick={() => handleOptionClick(option.value)}>
                  {I18n.t(option.labelKey)}
                </Item>)}
            </ViewportDropdownMenu>
          </Dropdown>
        </DropdownContainer>
      </TextContainer>
      {recordingDefaultOption === 'HOW_SCHEDULED' && <div>
          <CheckboxWrapper>
            <FormStyledCheckboxLabel>
              <CheckboxInput role="checkbox" checked={scheduledFromCalendlyCheckbox} onChange={() => setScheduledFromCalendlyCheckbox(!scheduledFromCalendlyCheckbox)} />
            </FormStyledCheckboxLabel>
            <CheckboxCopyWrapper>
              <BodyText size="small">
                {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.scheduled_from_calendly')}
              </BodyText>
            </CheckboxCopyWrapper>
          </CheckboxWrapper>
          {scheduledFromCalendlyCheckbox && <div className={eventTypeDropdownSpacingCss}>
              <DropdownWrapper>
                <EventTypeSelect placeholder={I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.no_event_types_placeholder')} disabled={false} eventTypes={eventTypes} onChange={handleEventTypeSelect} handleSelectAll={handleSelectAll} handleClearAll={handleClearAll} handleFetchMoreEvents={handleFetchMoreEvents} actualSelectedCount={actualSelectedCount} isWide />
                {hasEventError && <DropdownErrorWrapper>
                    <BodyText color={errorColor} size="medium">
                      {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.dropdown_error_text')}
                    </BodyText>
                  </DropdownErrorWrapper>}
              </DropdownWrapper>
            </div>}
          {isValidCalendar && <CheckboxWrapper>
              <FormStyledCheckboxLabel>
                <CheckboxInput role="checkbox" checked={scheduledFromNonCalendlyCheckbox} onChange={() => setScheduledFromNonCalendlyCheckbox(!scheduledFromNonCalendlyCheckbox)} />
              </FormStyledCheckboxLabel>
              <CheckboxCopyWrapper>
                <BodyText size="small">
                  {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.scheduled_from_calendar', {
              email: email || ''
            })}
                </BodyText>
              </CheckboxCopyWrapper>
            </CheckboxWrapper>}
        </div>}
    </div>;
};

require("./recording_defaults_body.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./recording_defaults_body.js");